import axios from 'axios';
import {
  applySpec,
  compose,
  path,
  prop,
  when,
  map,
  pathOr,
  concat,
  propOr,
  head,
  sort,
  ifElse,
} from 'ramda';
import { IdmuserinfoModel, TeamsModel, ProtektorRolesModel } from '../../models';
import config from '../../config';
import {
  handleGeneralError,
  handleHttpErrors,
  RequestFailureError,
  UnknownResponseError,
} from '../errors';
import validator from '../../util/passwordValidation';
import { resolver } from '../util';
import { getToken, addPropOr } from '../../util';
import { getTeamScopedUser } from '../Teams/resolvers';
import { sendUserAddedToTeamEmail } from '../../correspondence';

const revokeUser = resolver.unauthenticatedResolver.createResolver(async (root, { userID }) => {
  const token = await getToken('portaltoken');
  try {
    await axios({
      method: 'delete',
      url: `${config.authServer}/v1.1/user/${userID}`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return { user_id: userID };
  } catch (error) {
    if (error.response) {
      return handleHttpErrors(error);
    }
    if (error.request) {
      throw new RequestFailureError();
    } else {
      throw new UnknownResponseError();
    }
  }
});

const changePassword = resolver.authenticatedResolver.createResolver(
  async (root, { email, currentPassword, newPassword }) => {
    const token = await getToken('reports');
    if (validator.validate(newPassword) !== true) {
      throw Error(
        'Password must be atleast 6 characters long and must include 1 uppercase, lowercase, and number.',
      );
    }
    try {
      await axios({
        method: 'put',
        url: `${config.authServer}/v1.1/password`,
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: {
          email,
          current_password: currentPassword,
          new_password: newPassword,
        },
      });
      return true;
    } catch (error) {
      if (error.response) {
        return handleHttpErrors(error);
      }
      if (error.request) {
        throw new RequestFailureError();
      } else {
        throw new UnknownResponseError();
      }
    }
  },
);

const currentUser = resolver.authenticatedResolver.createResolver((root, args, { user }) => axios({
  method: 'get',
  url: `${config.authServer}/c/oauth/v1/userinfo`,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${user.access_token}`,
  },
})
  .then(
    applySpec({
      user_id: path(['data', 'user_id']),
      user_name: path(['data', 'user_name']),
      first_name: path(['data', 'first_name']),
      last_name: path(['data', 'last_name']),
      middle_name: path(['data', 'middle_name']),
      nickname: path(['data', 'nickname']),
      preferred_username: path(['data', 'preferred_username']),
      profile_url: path(['data', 'profile_url']),
      website: path(['data', 'website']),
      gender: path(['data', 'gender']),
      birthday: path(['data', 'birthday']),
      zoneinfo: path(['data', 'zoneinfo']),
      locale: path(['data', 'locale']),
      phone_number: path(['data', 'phone_number']),
      phone_number_verified: path(['data', 'phone_number_verified']),
      address: path(['data', 'address']),
      updated_at: compose(
        when(_ => _, _ => new Date(_).toString()),
        path(['data', 'updated_at']),
      ),
      email: path(['data', 'email']),
      email_validated: path(['data', 'email_validated']),
      avatar: path(['data', 'avatar']),
      roles: path(['data', 'roles']),
    }),
  )
  .catch((error) => {
    if (error.response) {
      return handleHttpErrors(error);
    }
    if (error.request) {
      throw new RequestFailureError();
    } else {
      throw new UnknownResponseError();
    }
  }));

const changeAvatar = resolver.authenticatedResolver.createResolver(
  async (root, { avatar, userId }) => {
    try {
      const update = {
        $set: {
          'auminfo.avatar': avatar,
        },
      };
      const Idmuserinfo = IdmuserinfoModel();
      await Idmuserinfo.updateOne({ user_id: userId }, update);
      const newAvatar = await Idmuserinfo.findOne({ user_id: userId });
      return newAvatar;
    } catch (err) {
      return handleGeneralError(err);
    }
  },
);

const editUserProfile = resolver.authenticatedResolver.createResolver(
  async (root, {
    userName, firstName, lastName, userId,
  }) => {
    try {
      const update = {
        $set: {
          'auminfo.user_name': userName,
          'auminfo.first_name': firstName,
          'auminfo.last_name': lastName,
        },
      };
      const Idmuserinfo = IdmuserinfoModel();
      await Idmuserinfo.updateOne({ user_id: userId }, update);
      const revisedUser = await Idmuserinfo.findOne({ user_id: userId });
      return revisedUser;
    } catch (err) {
      return handleGeneralError(err.message);
    }
  },
);

const disableUser = resolver.authenticatedResolver.createResolver(
  async (root, { disabledDesc, isDisabled, userId }) => {
    try {
      const update = {
        $set: {
          'auminfo.disabled': isDisabled,
          'auminfo.disabledDesc': disabledDesc,
          'auminfo.disabledTime': Date.now(),
        },
      };
      const Idmuserinfo = IdmuserinfoModel();
      const revisedUser = await Idmuserinfo.findOneAndUpdate({ user_id: userId }, update, {
        new: true,
      });
      return revisedUser;
    } catch (err) {
      return handleGeneralError(err.message);
    }
  },
);

const editUserRoles = resolver.authenticatedResolver.createResolver(
  async (root, { roles, userId }) => {
    try {
      const update = {
        $set: {
          'auminfo.roles': roles,
        },
      };
      const Idmuserinfo = IdmuserinfoModel();
      await Idmuserinfo.updateOne({ user_id: userId }, update);
      const revisedUser = await Idmuserinfo.findOne({ user_id: userId });
      return revisedUser;
    } catch (err) {
      return handleGeneralError(err.message);
    }
  },
);

const user = resolver.authenticatedResolver.createResolver(async (root, { userID }) => {
  const Idmuserinfo = IdmuserinfoModel();
  let foundUser;

  try {
    foundUser = await Idmuserinfo.findUserByUserID(userID);
  } catch (error) {
    if (error) {
      return handleGeneralError(error);
    }
  }

  return foundUser;
});

const users = resolver.authenticatedResolver.createResolver(
  async (root, { filter, skip, limit }) => {
    const Idmuserinfo = IdmuserinfoModel();
    const findPortalUsers = Idmuserinfo.findPortalUsers(filter, skip, limit).lean();
    const findPortalUsersCount = Idmuserinfo.findPortalUsersCount(filter).lean();
    let foundUsers;
    let foundUsersCount;

    try {
      [foundUsers, foundUsersCount] = await Promise.all([findPortalUsers, findPortalUsersCount]);
    } catch (error) {
      if (error) {
        return handleGeneralError(error);
      }
    }

    foundUsers.forEach((item) => {
      if (item.auminfo.phone_number_verified === '') {
        item.auminfo.phone_number_verified = false; // eslint-disable-line no-param-reassign
      }
    });
    return {
      users: foundUsers,
      skip,
      limit,
      count: foundUsersCount,
    };
  },
);

const domainUsers = resolver.authenticatedResolver.createResolver(
  async (root, {
    domain, filter, skip, limit,
  }) => {
    const Idmuserinfo = IdmuserinfoModel();
    const findPortalUsers = Idmuserinfo.findPortalUsersForDomain(
      domain,
      filter,
      skip,
      limit,
    ).lean();
    const findPortalUsersCount = Idmuserinfo.findPortalUsersCount(domain, filter).lean();
    let foundUsers;
    let foundUsersCount;

    try {
      [foundUsers, foundUsersCount] = await Promise.all([findPortalUsers, findPortalUsersCount]);
    } catch (error) {
      if (error) {
        return handleGeneralError(error);
      }
    }

    // Find over all latest login time.  We must look on both legacy and OAuth API login times
    const getLoginTimeList = map(propOr(0, 'login_time'));
    const allTimes = obj => concat(
      getLoginTimeList(pathOr([], ['auminfo', 'last_login_info'], obj)),
      getLoginTimeList(pathOr([], ['auminfo', 'last_oauth2_login_info'], obj)),
    );
    const headOr = (defaultValue, list) => ifElse(head, head, () => defaultValue)(list);
    const getLatestLoginTime = obj => headOr(0, sort((a, b) => b - a, allTimes(obj)));

    // Add these properties with default values if they are missing.
    const adjustDisableFields = compose(
      addPropOr(0, ['auminfo', 'disabledTime']),
      addPropOr('', ['auminfo', 'disabledDesc']),
      addPropOr(false, ['auminfo', 'disabled']),
    );

    const adjustLastLoginFields = (obj) => {
      const lastLogin = getLatestLoginTime(obj);
      return addPropOr(lastLogin, ['auminfo', 'last_login_time'], obj);
    };

    let adjustedUsers = map(adjustDisableFields, foundUsers);
    adjustedUsers = map(adjustLastLoginFields, adjustedUsers);

    return {
      users: adjustedUsers,
      skip,
      limit,
      count: foundUsersCount,
    };
  },
);

const associateUserWithTeam = resolver.authenticatedResolver.createResolver(
  async (root, { userId, teamId, role }) => {
    const Idmuserinfo = IdmuserinfoModel();
    const Teams = TeamsModel();
    const ProtektorRoles = ProtektorRolesModel();

    await Idmuserinfo.removeUserFromTeam(userId, teamId).lean();
    const addedUser = await Idmuserinfo.addUserToTeam(userId, teamId, role).lean();
    const scopedUser = getTeamScopedUser(teamId, addedUser);

    try {
      const [
        team,
        {
          roleIdentifier: { displayName: roleName },
        },
      ] = await Promise.all([
        Teams.findTeamById(teamId),
        ProtektorRoles.findRole({ 'roleIdentifier.name': role }),
      ]);
      const teamName = prop('display_name', team);
      const email = path(['auminfo', 'email'], scopedUser);
      await sendUserAddedToTeamEmail(email, teamName, roleName);
    } catch (error) {
      /* User will not receive the email, but the update was successful */
    }

    return scopedUser;
  },
);

const removeUserFromTeam = resolver.authenticatedResolver.createResolver(
  async (root, { userId, teamId }) => {
    const Idmuserinfo = IdmuserinfoModel();

    const foundUser = await Idmuserinfo.removeUserFromTeam(userId, teamId).lean();

    return getTeamScopedUser(teamId, foundUser);
  },
);

export default {
  Query: {
    currentUser,
    user,
    users,
    domainUsers,
  },
  Mutation: {
    editUserProfile,
    editUserRoles,
    changeAvatar,
    changePassword,
    revokeUser,
    associateUserWithTeam,
    removeUserFromTeam,
    disableUser,
  },
};
